import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search } from "lucide-react";

/**
 * Checkbox multi-select dropdown with "Select All", search, and Clear/Done
 * actions. The entire option row is clickable (not just the checkbox), and all
 * state changes are pure client-side — callers should avoid firing network
 * requests per click if they want this to feel instant.
 *
 * The open panel is rendered into a portal on <body> and positioned with
 * `fixed` coordinates computed from the trigger button — this makes it immune
 * to clipping by any ancestor's `overflow: hidden`/`auto` (a real bug we hit:
 * a wrapping card with overflow:hidden was cutting off the last filter's
 * options), regardless of where this component gets used in the future.
 */
export default function MultiSelectDropdown({ options = [], value = [], onChange, disabled = false, placeholder = "Select…", singleSelect = false }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [rect, setRect] = useState(null);
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        function onOutside(e) {
            if (
                rootRef.current && !rootRef.current.contains(e.target) &&
                panelRef.current && !panelRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        }
        function onEscape(e) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onOutside);
        document.addEventListener("keydown", onEscape);
        return () => {
            document.removeEventListener("mousedown", onOutside);
            document.removeEventListener("keydown", onEscape);
        };
    }, []);

    // Track the trigger button's position while open so the portal-rendered
    // panel stays anchored under it through scrolling/resizing.
    useLayoutEffect(() => {
        if (!open) return;
        const update = () => {
            if (rootRef.current) setRect(rootRef.current.getBoundingClientRect());
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [open]);

    // Reset search each time the panel opens, and focus the search box.
    useEffect(() => {
        if (open) {
            setQuery("");
            setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => String(o).toLowerCase().includes(q));
    }, [options, query]);

    const allSelected = options.length > 0 && value.length === options.length;
    const allFilteredSelected = filtered.length > 0 && filtered.every((o) => value.includes(o));

    const toggleAll = () => {
        if (query.trim()) {
            if (allFilteredSelected) onChange(value.filter((v) => !filtered.includes(v)));
            else onChange([...new Set([...value, ...filtered])]);
        } else {
            onChange(allSelected ? [] : [...options]);
        }
    };
    const toggleOne = (opt) => {
        if (singleSelect) {
            onChange([opt]);
            setOpen(false);
        } else {
            onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
        }
    };

    // A single selected value always shows its name, even when that happens to
    // be every available option (e.g. a filter narrowed down to just one
    // choice) — otherwise it renders the uninformative "All selected (1)"
    // instead of the actual value, which is what the user is really asking to see.
    const summary =
        value.length === 0
            ? placeholder
            : value.length === 1
            ? value[0]
            : value.length === options.length && options.length > 0
            ? `All selected (${options.length})`
            : `${value.length} of ${options.length} selected`;

    return (
        <div ref={rootRef} style={{ position: "relative" }}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                style={{
                    width: "100%", padding: "9px 12px", borderRadius: 6,
                    border: open ? "1px solid #032B4E" : "1px solid #d1d5db",
                    outline: "none", fontSize: 13, background: disabled ? "#f3f4f6" : "#fff",
                    color: value.length ? "#1f2937" : "#9ca3af", boxSizing: "border-box",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    cursor: disabled ? "not-allowed" : "pointer", textAlign: "left",
                    fontWeight: value.length ? 600 : 400,
                }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {summary}
                </span>
                <ChevronDown
                    size={14}
                    color="#6b7280"
                    style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }}
                />
            </button>

            {open && !disabled && rect && createPortal(
                <div
                    ref={panelRef}
                    style={(() => {
                        const viewportH = window.innerHeight;
                        const spaceBelow = viewportH - rect.bottom;
                        const spaceAbove = rect.top;
                        const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
                        const maxPanelHeight = Math.min(320, Math.max(160, (openUp ? spaceAbove : spaceBelow) - 12));
                        return {
                            position: "fixed", zIndex: 9999,
                            left: rect.left, width: rect.width,
                            ...(openUp ? { bottom: viewportH - rect.top + 4 } : { top: rect.bottom + 4 }),
                            background: "#fff", border: "1px solid #d1d5db", borderRadius: 8,
                            boxShadow: "0 10px 24px rgba(3,43,78,0.16)", overflow: "hidden",
                            display: "flex", flexDirection: "column", maxHeight: maxPanelHeight,
                        };
                    })()}
                >
                    {/* Search (only useful for longer lists, but harmless always) */}
                    {options.length > 6 && (
                        <div style={{ padding: "8px 10px", borderBottom: "1px solid #eef0f2", background: "#fafbfc", position: "relative" }}>
                            <Search size={13} color="#9ca3af" style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)" }} />
                            <input
                                ref={searchRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search…"
                                style={{
                                    width: "100%", boxSizing: "border-box", padding: "6px 8px 6px 28px",
                                    borderRadius: 6, border: "1px solid #e2e8f0", outline: "none",
                                    fontSize: 12.5, background: "#fff",
                                }}
                            />
                        </div>
                    )}

                    {/* Select All (hidden in singleSelect mode) */}
                    {!singleSelect && (
                        <div
                            onClick={toggleAll}
                            style={{
                                display: "flex", alignItems: "center", gap: 8, padding: "9px 12px",
                                borderBottom: "1px solid #eef0f2", fontWeight: 700, fontSize: 12.5,
                                cursor: "pointer", color: "#032B4E", background: "#fafbfc", userSelect: "none",
                            }}
                        >
                            <CheckboxVisual
                                checked={query.trim() ? allFilteredSelected : allSelected}
                                indeterminate={!allSelected && value.length > 0 && !query.trim()}
                            />
                            {query.trim() ? `Select matching (${filtered.length})` : `Select All (${options.length})`}
                        </div>
                    )}

                    {/* Options */}
                    <div style={{ overflowY: "auto", flex: 1 }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: "14px 12px", fontSize: 12.5, color: "#9ca3af" }}>
                                {options.length === 0 ? "No options available" : "No matches"}
                            </div>
                        ) : (
                            filtered.map((opt) => {
                                const checked = value.includes(opt);
                                return (
                                    <div
                                        key={opt}
                                        onClick={() => toggleOne(opt)}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                                            fontSize: 13, color: "#374151", cursor: "pointer", userSelect: "none",
                                            background: checked ? "#f0f7ff" : "transparent",
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = checked ? "#e3f0fc" : "#f8fafc")}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = checked ? "#f0f7ff" : "transparent")}
                                    >
                                        {!singleSelect && <CheckboxVisual checked={checked} />}
                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer actions */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 10px",
                        borderTop: "1px solid #eef0f2", background: "#fafbfc",
                    }}>
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            style={{
                                background: "transparent", border: "none", color: "#6b7280",
                                fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: "4px 8px",
                            }}
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            style={{
                                background: "#032B4E", border: "none", color: "#fff", borderRadius: 6,
                                fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: "5px 16px",
                            }}
                        >
                            Done
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

function CheckboxVisual({ checked, indeterminate }) {
    return (
        <span
            aria-hidden
            style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                border: checked || indeterminate ? "1px solid #032B4E" : "1px solid #cbd5e1",
                background: checked || indeterminate ? "#032B4E" : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.12s ease", pointerEvents: "none",
            }}
        >
            {checked && <Check size={12} color="#fff" strokeWidth={3} />}
            {!checked && indeterminate && <span style={{ width: 8, height: 2, background: "#fff", borderRadius: 1 }} />}
        </span>
    );
}
